import { inject, Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LazyLoadEvent } from 'primeng/api';
import {
  ServiceLineRule,
  RoutingUser,
} from '@shared/models/service-line-rule.model';
import { AuthorizationCaseRule, RulesReferences } from '@products/dmepos/models/authorization-case-rule.model';
import { API_CONTEXT_TOKEN } from '@core/constants';
import { API_URL } from '@core/config';
import { ITEM_ROOT } from 'odata-query';
import { RulesBaseService } from '@core/services/rules/rules-base.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PlatformService } from '@core/services/platform.service';

@Injectable({
  providedIn: 'root',
})
export class CaseRulesService extends RulesBaseService {
  private readonly apiUrl = `${inject(API_URL)}dmepos`;

  uniqueKey = 'authorizationCaseRuleId';

  private readonly platformService = inject(PlatformService);

  get apiUrlLob(): string {
    return `${this.apiUrl}/lob/${this.platformService.currentLineOfBusiness$.value?.lineOfBusinessId}`;
  }

  query(rawQuery: LazyLoadEvent): Observable<any> {
    const query = this.buildQuery(rawQuery);
    const context = new HttpContext();

    context.set(API_CONTEXT_TOKEN, 'queue');

    return this.http.post<AuthorizationCaseRule>(`${this.apiUrlLob}/AuthorizationCaseRules/$query`, `"${query}"`, {
      ...this.options,
      context,
    });
  }

  add(payload: ServiceLineRule): Observable<ServiceLineRule> {
    return this.http.post<ServiceLineRule>(`${this.apiUrlLob}/AuthorizationCaseRules`, this.normalizeRule(payload));
  }

  update(id: string, payload: ServiceLineRule): Observable<ServiceLineRule> {
    return this.http.put<ServiceLineRule>(`${this.apiUrlLob}/AuthorizationCaseRules/${id}`, this.normalizeRule(payload));
  }

  delete(id: string): Observable<null> {
    return this.http.delete<null>(`${this.apiUrlLob}/AuthorizationCaseRules/${id}`);
  }

  getReferences(): Observable<RulesReferences> {
    return this.http.get<RulesReferences>(`${this.apiUrlLob}/AuthorizationCaseRules/references`, this.options);
  }

  getUsers(): Observable<RoutingUser[]> {
    return this.http.get<RoutingUser[]>(`${this.apiUrlLob}/users/operations`, this.options);
  }

  createForm() {
    return new FormGroup({
      authorizationCaseRuleId: new FormControl(null),
      lineOfBusinessId: new FormControl(null),
      user: new FormControl(null, [Validators.required]),
      specialty: new FormControl(null),
      secondLevelReview: new FormControl(null),
      totalLimit: new FormControl(null, [Validators.min(0), Validators.max(999999), Validators.required]),
      totalCount: new FormControl(null),
      categories: new FormControl([]),
      mco: new FormControl([]),
      states: new FormControl([]),
      tenantName: new FormControl(null),
      isActive: new FormControl(true),
      status: new FormControl(null),
      userWarnings: new FormControl({}),
    });
  }

  getUserWarnings(rule: AuthorizationCaseRule) {
    const { user: { status }, authorizationCaseRuleId } = rule;
    const hasWarnings = Boolean(authorizationCaseRuleId && status !== 'Active');

    return {
      error: hasWarnings,
      message: 'User permissions error.\nThe user cannot receive DMEPOS lines.',
    };
  }

  override rawQueryToOdataObject = (key: string, value: string[], matchMode: string) => {
    switch (key) {
      case 'userId':
        if (matchMode === 'not') {
          return {
            [matchMode]: {
              user: { [key]: { ['in']: value } },
            },
          };
        }

        return {
          user: { [key]: { [matchMode]: value } },
        };
      case 'secondLevelReview':
      case 'specialty':
      case 'tenantName':
        return { [key]: { [matchMode]: value } };
      case 'categories':
      case 'mco':
      case 'states':
        const index = value.indexOf('null');
        const nullExists = index > -1;

        if (nullExists) {
          value.splice(index, 1);
        }

        const collection = {
          [key]: {
            any: { [ITEM_ROOT]: { [matchMode]: value } },
          },
        };

        if (nullExists) {
          const or = value.length > 0;

          if (or) {
            return {
              or: [{ not: { [key]: { any: [] } } }, collection],
            };
          } else {
            return { not: { [key]: { any: [] } } };
          }
        }

        return collection;
      case 'isActive':
        return {
          [key]: value,
        };
      default:
        return {};
    }
  };

  private normalizeRule(data: any) {
    delete data.totalCount;
    delete data.status;

    return {
      ...data,
      user: {
        userId: data.user.userId,
      },
    };
  }
}
